import UtilOrder from '@pages/OrganizedSales/CreateOrders/utilOrder';

import { RootState } from 'app/store';
import React, { useEffect, useRef } from 'react';
import toast, { useToaster } from 'react-hot-toast/headless';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getUnreadNotiAmount } from '@slice/orderManager.slice';
import PerfectScrollbar from 'react-perfect-scrollbar';

import { useFcmToken } from './useFcmToken';
import { useOrderNotification } from './useOrderNotification';
import { useFcmMessages } from './useFcmMessages';
import { useTicketNotification } from './useTicketNotification';

type IBody = {
  currentOrderStatus: string; // đơn
  maDonHang: string; // đơn
  orderTypeId: string; // đơn

  staffReceive: string; // đơn
  staffSend: string; // đơn
  staffBehavior: string; // đơn + phiếu

  Tag?: string; // phiếu
  State?: string; // phiếu
  UserTrigger?: string; // phiếu
  TicketCodes?: string; // phiếu
  TicketIds?: string; // phiếu
};

export interface INotification {
  title: string;
  body: IBody;
  maDonHang: string;
  idPhieu?: string | number;
  maPhieu?: string | number;
}

const Notifications = () => {
  const { t } = useTranslation();
  const { toasts, handlers } = useToaster();
  const { startPause, endPause, calculateOffset, updateHeight } = handlers;

  const hover = useRef<any>(null);

  const handleDismiss = (id: any) => {
    toast.remove(id);
  };

  const handleCloseAll = () => {
    toast.dismiss();
    toast.remove();
  };

  const onMouseEnter = () => {
    hover.current = true;
    startPause();
  };

  const onMouseLeave = () => {
    hover.current = false;
    endPause();
  };

  return (
    <div
      style={{
        zIndex: 10000,
        position: 'fixed',
        top: 75,
        right: 12, // cách mép phải để popup không sát / bị cắt ở mép màn hình
        display: toasts.length > 0 ? 'unset' : 'none'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {hover.current && (
        <div
          style={{ zIndex: 10001 }}
          className="bg-orange-100 absolute px-2 -top-5 right-[10px] hover:underline flex justify-between select-none"
          onClick={() => {
            handleCloseAll();
          }}
        >
          {/* <div className="whitespace-nowrap">{t('Notification.Title')}</div> chưa cần dùng để tạm ẩn đây */}
          {`${'❌ '}`}
          <span className="whitespace-nowrap text-red-900  hover:text-red-600 cursor-pointer">
            {t('Notification.CloseAll')} {`${' '}`} {`(${toasts.length})`}
          </span>
        </div>
      )}

      <PerfectScrollbar
        className="absolute notify-order-wapper"
        style={{
          width: '250px',
          minHeight: '0',
          height:
            toasts.length > 3
              ? '56vh'
              : toasts.length > 2
              ? '46vh'
              : toasts.length > 1
              ? '31vh'
              : toasts.length > 0
              ? '16vh'
              : 0,
          top: '0%',
          right: '0%',
          paddingBottom: '10px'
        }}
      >
        {toasts.map(toast => {
          const offset = calculateOffset(toast, {
            reverseOrder: false,
            gutter: 8
          });

          const ref = el => {
            if (el && typeof toast.height !== 'number') {
              const height = el.getBoundingClientRect().height;
              updateHeight(toast.id, height);
            }
          };

          const child: any = toast.message;

          // tạo style custom màu theo thêm luôn!!
          return (
            <div
              key={toast.id}
              ref={ref}
              style={{
                zIndex: 1000,
                position: 'absolute',
                width: '240px',
                minWidth: '240px',
                maxWidth: '240px',
                background: 'papayawhip',
                transition: 'all 0.5s ease-out',
                opacity: toast.visible ? 1 : 0,
                transform: `translateY(${offset}px)`,
                padding: '10px',
                paddingBottom: '20px', // chừa chỗ cho nút "Bỏ qua" (absolute ở đáy) không đè lên dòng chữ cuối
                borderBottomRightRadius: '5px',
                borderTopLeftRadius: '5px',
                borderBottomLeftRadius: '5px'
              }}
            >
              <button
                className="absolute -bottom-0.5 right-1 text-red-600 underline hover:text-red-500 cursor-pointer"
                onClick={() => handleDismiss(toast.id)}
              >
                {t('Notification.Dismiss')}
              </button>
              {child}
            </div>
          );
        })}
      </PerfectScrollbar>
    </div>
  );
};

export interface IDivToast {
  key?: any;
  handleDetail: (maDonHang?: any, orderTypeId?: number, isTransactionRoute?: boolean) => any;
  handleMenu: (maDonHang?: any, orderTypeId?: number, isTransactionRoute?: boolean) => any;
  handleQActions: (maDonHang?: any, orderTypeId?: number) => any;
  notification?: INotification;
}

const normalizeTitle = (value?: string) => {
  return (value ?? '').trim().toLowerCase().normalize('NFC');
};

const getTransactionSubTitle = ({ title, isWarehouse }: { title?: string; isWarehouse: boolean }) => {
  const transactionTitle = normalizeTitle(title);
  console.log({ transactionTitle, title });

  if (transactionTitle.includes('cấp')) {
    return isWarehouse ? 'Xác nhận/từ chối cấp phát.' : 'Có lệnh cấp phát.';
  }

  if (transactionTitle.includes('trao đổi')) {
    return isWarehouse ? 'Đã xác nhận/từ chối trao đổi.' : 'Có lệnh trao đổi.';
  }

  if (transactionTitle.includes('trả hàng')) {
    return isWarehouse ? 'Có lệnh trả hàng.' : 'Đã xác nhận/từ chối trả hàng.';
  }

  if (transactionTitle.includes('cập nhật')) {
    return isWarehouse ? 'Có yêu cầu cập nhật.' : 'Đã xác nhận/từ chối cập nhật.';
  }

  if (transactionTitle.includes('thu hồi')) {
    return isWarehouse ? 'Có lệnh thu hồi.' : 'Đã xác nhận/từ chối thu hồi.';
  }

  return '';
};

// sau sẽ dùng cho thông báo đã nhận !!
export function DivToast(props: IDivToast) {
  const { t } = useTranslation();
  const uLogin = useSelector((state: RootState) => state.user.account);
  const navigate = useNavigate();

  // Thông báo phiếu hỗ trợ (có idPhieu) dùng chung khung với đơn: hiện mã phiếu + trạng thái phiếu, bỏ nút Thao tác / Menu.
  // Bấm mã phiếu -> /quan-ly-phieu?ticketId=, trang Quản lý phiếu đọc query và tự mở phiếu.
  // TicketIds có thể là "12" hoặc "12,13" -> mở phiếu đầu tiên.
  const isTicket = !!props.notification?.idPhieu;
  const openTicket = () => {
    const id = String(props.notification?.idPhieu ?? '')
      .split(',')[0]
      .trim();
    if (!id) return;
    navigate(`/quan-ly-phieu?ticketId=${id}`);
    toast.remove(`ticket-${props.notification?.idPhieu}`); // id popup đặt ở useTicketNotification; mở rồi thì đóng như popup đơn
  };

  const body = props.notification?.body;
  const staffBehavior = body?.staffBehavior ?? '';

  const isTransaction = staffBehavior.includes('Transaction');
  const isWarehouse = isTransaction && uLogin?.role === 'WareHouse';

  const transactionSubTitle = isTransaction
    ? getTransactionSubTitle({
        title: props.notification?.title,
        isWarehouse
      })
    : '';

  const min = 0;
  const listIcon: any = [
    <span key="1">🌻</span>,
    <span key="2">🔥</span>,
    <span key="3">👏</span>,
    <span key="4">🌤️</span>,
    <span key="5">🙃</span>,
    <span key="6">💥</span>,
    <span key="7">✨</span>,
    <span key="8">💯</span>,
    <span key="9">⭐</span>,
    <span key="10">🎉</span>,
    <span key="11">🍻</span>,
    <span key="12">🎈</span>,
    <span key="13">💛</span>,
    <span key="14">🖖</span>,
    <span key="15">👍</span>
  ];

  const formatStatus = isTicket
    ? UtilOrder.convertStatusTicket(props.notification?.body?.State)
    : UtilOrder.convertStatusTextToStatusClient(
        props.notification?.body?.currentOrderStatus,
        props.notification?.body?.staffBehavior == 'OperatorSeen' || false
      );

  // Tiêu đề phiếu bỏ chữ "Nhân viên" ở đầu cho gọn (tên NV đã hiện ở dòng cuối), viết hoa chữ đầu.
  // Vd "Nhân viên tiếp tục xử lý phiếu hỗ trợ" -> "Tiếp tục xử lý phiếu hỗ trợ"
  const ticketTitle = (props.notification?.title || '').replace(/^\s*nhân viên\s+/i, '');
  const notificationLabel = isTicket
    ? ticketTitle.charAt(0).toUpperCase() + ticketTitle.slice(1)
    : staffBehavior.includes('Transaction')
    ? props.notification?.title
    : t('Notification.Order');

  return (
    <div key={props.key} className="cursor-normal w-full">
      {/* Cho xuống dòng: tiêu đề dài (thông báo phiếu / giao dịch kho) không tràn khỏi khung 240px */}
      <div className="font-semibold !text-sm break-words">
        {listIcon[Math.floor(Math.random() * (listIcon.length - min)) + min]}
        {notificationLabel}:{' '}
        <span
          className="text-red-600 hover:underline hover:text-red-500 cursor-pointer"
          onClick={() => {
            if (isTicket) openTicket();
            else
              props.handleDetail(
                props.notification?.maDonHang,
                Number(props.notification?.body.orderTypeId),
                !!transactionSubTitle
              );
          }}
        >
          {isTicket ? props.notification?.body?.TicketCodes : props.notification?.body?.maDonHang}
        </span>
      </div>
      <div className="select-none">
        <span
          style={{ backgroundColor: `${formatStatus?.color}` }}
          className="relative text-sm whitespace-nowrap text-white w-fit h-fit border rounded-sm px-1 "
        >
          {formatStatus?.seen == false && (
            <div className="absolute w-[10px] h-[10px] bg-[#FFD23F] -top-[4px] -right-[4px]  rounded-full"></div>
          )}
          {t(formatStatus?.text)}
        </span>
        {'  '}
        {!transactionSubTitle && !isTicket && (
          <>
            {'  '}
            <span
              className="text-blue-500 underline hover:text-blue-400 cursor-pointer"
              onClick={() => {
                props.handleQActions(props.notification?.maDonHang, +props.notification?.body.orderTypeId);
              }}
            >
              {t('Notification.QAction')}
            </span>
          </>
        )}
        {'  '}
        {isTicket ? (
          <span className="text-blue-500 underline hover:text-blue-400 cursor-pointer" onClick={openTicket}>
            Xem phiếu
          </span>
        ) : (
          <span
            className="text-blue-500 underline hover:text-blue-400 cursor-pointer"
            onClick={() => {
              props.handleMenu(
                props.notification?.maDonHang,
                +props.notification?.body.orderTypeId,
                !!transactionSubTitle
              );
            }}
          >
            {t('Notification.Menu')}
          </span>
        )}
      </div>
      <div className="select-none">
        <span className="font-semibold"> {props.notification?.body?.staffSend}</span>
        {' – '}
        <span className="">
          {transactionSubTitle
            ? transactionSubTitle
            : t('OrganizedSales.OrderManager.ModifyOrder.TimelineStatus.' + props.notification?.body?.staffBehavior)}
        </span>
      </div>
    </div>
  );
}

// thử nghiệm code thông báo mới ..
export default function WapperNotification() {
  // Hạ tầng dùng chung: đăng ký + gia hạn FCM token
  useFcmToken();

  // Hai nghiệp vụ riêng
  const { onOrderMessage } = useOrderNotification();
  const { onTicketMessage } = useTicketNotification();

  // Số thông báo chưa đọc (GetAllUnreadAmount): lấy khi vào app và mỗi lần có thông báo mới
  const dispatch = useDispatch<any>();
  // TẠM TẮT gọi GetAllUnreadAmount , bật lại thì bỏ comment 2 chỗ dưới
  // useEffect(() => {
  //   dispatch(getUnreadNotiAmount());
  // }, []);

  // Điểm nghe FCM duy nhất: gồm cả chạy nền (BroadcastChannel) và foreground
  useFcmMessages(msg => {
    // dispatch(getUnreadNotiAmount());
    // nhánh mã phiếu
    if (msg.idPhieu) {
      onTicketMessage(msg);
      return;
    }

    // chạy cho đơn
    onOrderMessage(msg);

    // TẠM: backend chưa gửi idPhieu nên ticket vẫn nhận chung như code cũ.
    // Khi backend gửi idPhieu thì xoá dòng này. !!! lưu ý
    onTicketMessage(msg);
  });

  return <Notifications />;
}
